import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Send, X } from 'lucide-react';

/**
 * Modal for emailing PDF reports to recipients.
 * Extracted from Visits.jsx to keep the parent file small.
 */
export default function EmailReportsModal({
  open,
  onClose,
  emailRecipients,
  setEmailRecipients,
  emailReports,
  setEmailReports,
  emailMessage,
  setEmailMessage,
  emailSending,
  onSend,
}) {
  if (!open) return null;

  const reports = [
    { key: 'therapists', label: 'Therapists Weekly' },
    { key: 'agencies', label: 'Agencies Weekly' },
    { key: 'evalDc', label: 'Eval vs Discharge' },
    { key: 'lateNotes', label: 'Late Notes' },
    { key: 'missed', label: 'Missed Visits' },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => !emailSending && onClose()}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="email-reports-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Mail size={20} /> Email Reports
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={emailSending}>
            <X size={16} />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <Label className="mb-1.5 block text-sm font-medium">Recipients</Label>
            <Input
              placeholder="email1@company.com, email2@company.com"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              data-testid="email-recipients-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Separate multiple emails with commas</p>
          </div>

          <div>
            <Label className="mb-2 block text-sm font-medium">Select Reports to Attach</Label>
            <div className="space-y-2">
              {reports.map((report) => (
                <label
                  key={report.key}
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid={`email-report-${report.key}`}
                >
                  <input
                    type="checkbox"
                    checked={emailReports[report.key]}
                    onChange={(e) =>
                      setEmailReports((prev) => ({ ...prev, [report.key]: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{report.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm font-medium">Message (optional)</Label>
            <textarea
              rows={4}
              placeholder="Add a personal note that will appear above the metrics in the email body…"
              value={emailMessage || ''}
              onChange={(e) => setEmailMessage?.(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y min-h-[80px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="email-message-input"
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Shown at the top of the email, above the Weekly Metrics summary.
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            The email body will include the Weekly Metrics summary.
          </p>
        </div>

        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={emailSending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSend}
            disabled={emailSending}
            className="gap-1.5"
            data-testid="send-email-button"
          >
            {emailSending ? (
              <>
                <span className="animate-spin">⟳</span> Generating &amp; Sending...
              </>
            ) : (
              <>
                <Send size={14} /> Send Email
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
